import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { 
  Heart, ArrowRight, MapPin,
  RefreshCw, Check, ChevronRight, Loader2,
  ImageIcon,
  Play, X as XIcon, ShieldCheck, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import CandleWall from '../components/CandleWall';
import SectionMedia from '../components/SectionMedia';
import { COUNTRY_FLAGS, ImpactTracker, ProgramOverview, ThreePillarSystem, ScalingSection, WaterSection } from './sections/CountryProgramSections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CountryProgram = () => {
  const { programId } = useParams();
  const [searchParams] = useSearchParams();
  // `?crisis_preview={cid}` triggers admin-only preview mode: fetches
  // the campaign from the ADMIN endpoint (cookie-auth) instead of the
  // public one, and force-renders the crisis section regardless of
  // sections_enabled.emergency_support / show_publicly.
  const previewCid = searchParams.get('crisis_preview');
  const isPreview = !!previewCid;
  const [program, setProgram] = useState(null);
  const [globalContent, setGlobalContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [wallDisplay, setWallDisplay] = useState('anonymous');
  const [isProcessing, setIsProcessing] = useState(false);
  const [donorNote, setDonorNote] = useState('');
  const [candleCount, setCandleCount] = useState(0);
  const [media, setMedia] = useState([]);
  const [lightboxItem, setLightboxItem] = useState(null);

  const fetchMedia = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/program-media/${programId}`);
      setMedia(res.data || []);
    } catch (err) { logger.error('Error loading media:', err); }
  }, [programId]);

  const fetchCandleCount = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/candles/${programId}`);
      setCandleCount(res.data.count || 0);
    } catch (err) { logger.error('Error loading candle count:', err); }
  }, [programId]);

  const fetchProgram = useCallback(async () => {
    try {
      const [response, globalRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/country-programs/${programId}`),
        axios.get(`${BACKEND_URL}/api/public/site-content/global_country_program_content`),
      ]);
      setProgram(response.data);
      setGlobalContent(globalRes.data?.data || null);
      const highlighted = response.data.donation_tiers?.find(t => t.highlight);
      if (highlighted) setSelectedTier(highlighted);
    } catch (error) {
      logger.error('Error fetching program:', error);
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchProgram();
    fetchCandleCount();
    fetchMedia();
  }, [fetchProgram, fetchCandleCount, fetchMedia]);

  const currentAmount = selectedTier?.amount || (customAmount ? parseFloat(customAmount) : 0);

  const handleDonate = async () => {
    if (currentAmount <= 0) return;
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/donations/checkout`, {
        origin_url: window.location.origin,
        package_type: selectedTier?.id || 'custom',
        custom_amount: selectedTier ? null : currentAmount,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        is_recurring: isRecurring,
        recurring_interval: 'month',
        wall_display: wallDisplay,
        country_program_id: programId,
        donor_note: donorNote
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      logger.error('Error creating checkout:', error);
      alert('Error processing donation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const [waterProcessing, setWaterProcessing] = useState(false);
  const handleWaterDonate = async (quantity) => {
    setWaterProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/donations/checkout`, {
        origin_url: window.location.origin,
        package_type: quantity === 1 ? 'water_1' : 'water_2',
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        is_recurring: false,
        wall_display: 'full_name',
        country_program_id: programId,
        donor_note: `Clean water system x${quantity} for ${program.country_name}`
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      logger.error('Error creating water checkout:', error);
      alert('Error processing donation. Please try again.');
    } finally {
      setWaterProcessing(false);
    }
  };

  // Emergency support one-tap donate. Passes `is_emergency=true` so the
  // finance ledger can split emergency vs. regular monthly giving.
  const [emergencyProcessing, setEmergencyProcessing] = useState(false);
  // Live impact snapshot of the linked crisis campaign. Only loaded
  // when the country program actually links a campaign; the endpoint
  // itself 404s unless `show_publicly=true`. In preview mode we hit
  // the admin endpoint (cookie-auth) and force-render regardless of
  // the public toggle.
  const [crisisPublic, setCrisisPublic] = useState(null);
  // Phase 3: all active + public + non-archived campaigns tied to this
  // country. Rendered as a stacked list right below ImpactTracker so
  // donors see every active drive (crisis, fundraiser, supply drive)
  // for that country simultaneously.
  const [countryCampaigns, setCountryCampaigns] = useState([]);
  useEffect(() => {
    if (!programId || isPreview) return;
    axios.get(`${BACKEND_URL}/api/public/campaigns/by-country/${programId}`)
      .then((r) => setCountryCampaigns(r.data.campaigns || []))
      .catch(() => setCountryCampaigns([]));
  }, [programId, isPreview]);
  useEffect(() => {
    // Preview mode: hit admin endpoint with the campaign id from the
    // query string. Ignores country program's linked crisis id so the
    // admin can preview a campaign that isn't linked yet.
    if (isPreview) {
      axios.get(`${BACKEND_URL}/api/admin/crisis-campaigns/${previewCid}`,
        { withCredentials: true })
        .then((r) => setCrisisPublic({
          ...r.data,
          // Force show_live_impact ON in preview so admins see the
          // widget in its final rendered form. The actual saved value
          // still determines what shows in production.
          show_live_impact: r.data.show_live_impact !== false,
        }))
        .catch(() => setCrisisPublic(null));
      return;
    }
    const cid = program?.emergency_support_crisis_campaign_id;
    if (!cid) { setCrisisPublic(null); return; }
    axios.get(`${BACKEND_URL}/api/public/crisis-campaigns/${cid}`)
      .then((r) => setCrisisPublic(r.data))
      .catch(() => setCrisisPublic(null));
  }, [program?.emergency_support_crisis_campaign_id, isPreview, previewCid]);

  // Scroll to the emergency section on first mount when in preview
  // mode so the admin lands right at the section they're previewing.
  useEffect(() => {
    if (!isPreview || !crisisPublic) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="emergency-support-section"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(t);
  }, [isPreview, crisisPublic]);

  // While in preview mode, offset the whole page by the banner height
  // so the Layout header (fixed top-0) isn't visually covered by the
  // admin bar. Cleared on unmount / mode change.
  useEffect(() => {
    if (!isPreview) return;
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = '40px';
    return () => { document.body.style.paddingTop = prev; };
  }, [isPreview]);

  const handleEmergencyDonate = async () => {
    const amount = program?.emergency_support_cost || 0;
    if (amount <= 0) {
      alert('Suggested donation amount is not set. Please contact the team.');
      return;
    }
    setEmergencyProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/donations/checkout`, {
        origin_url: window.location.origin,
        package_type: 'custom',
        custom_amount: amount,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        is_recurring: false,
        wall_display: 'full_name',
        country_program_id: programId,
        is_emergency: true,
        crisis_campaign_id: program.emergency_support_crisis_campaign_id || null,
        donor_note: `Emergency Support — ${program.emergency_support_title || program.country_name}`,
      });
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      logger.error('Error creating emergency checkout:', error);
      alert('Error processing donation. Please try again.');
    } finally {
      setEmergencyProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="py-20 text-center">
        <h2 className="font-heading text-2xl font-bold mb-4">Program Not Found</h2>
        <Link to="/international" className="text-[#BA1826] hover:underline">Back to International</Link>
      </div>
    );
  }

  const progressPercentage = program.monthly_goal > 0 
    ? Math.min((program.amount_raised / program.monthly_goal) * 100, 100) 
    : 0;

  const useGlobal = program.use_global_content !== false && globalContent && globalContent.food_bucket;

  // Global items use {name, quantity}; BucketCard expects {item, quantity}
  const mapGlobalItems = (items) => (items || []).map((it) => ({ item: it.name, quantity: it.quantity }));

  const foodItems = useGlobal
    ? mapGlobalItems(globalContent.food_bucket?.items)
    : (program.bucket_contents_food || []);
  const hygieneItems = useGlobal
    ? mapGlobalItems(globalContent.hygiene_kit?.items)
    : (program.bucket_contents_hygiene || []);
  const tiers = program.donation_tiers || [];
  const foodCost = useGlobal
    ? (globalContent.food_bucket?.total || 0)
    : (program.food_bucket_cost || 46);
  const hygieneCost = useGlobal
    ? (globalContent.hygiene_kit?.total || 0)
    : (program.hygiene_bucket_cost || 24);
  const waterCost = program.water_system_cost || 55;
  const fullFamilyCost = foodCost + hygieneCost + waterCost;
  const foodSize = useGlobal
    ? (globalContent.food_bucket?.size || '5-gallon')
    : (program.food_bucket_size || '5-gallon');
  const hygieneSize = useGlobal
    ? (globalContent.hygiene_kit?.size || '2-gallon')
    : (program.hygiene_bucket_size || '2-gallon');
  // Cost/Family auto-derives from Food + Hygiene totals when using Global Content
  const costPerFamily = useGlobal ? (foodCost + hygieneCost) : (program.cost_per_family || 0);

  const familiesFromDonations = costPerFamily > 0
    ? Math.floor(program.amount_raised / costPerFamily)
    : 0;

  // Section visibility helper — treat missing keys as visible so
  // programs that haven't been re-saved since the upgrade still render.
  const secOn = (key) => (program.sections_enabled?.[key] ?? true) !== false;
  // Emergency Support is off by default — only render when admin has
  // explicitly enabled it AND filled in copy. Preview mode forces it
  // on regardless so admins can review the section pre-publish.
  const emergencyOn = isPreview
    ? true
    : (program.sections_enabled?.emergency_support === true
        && (program.emergency_support_description || program.emergency_support_title));

  return (
    <div>
      {/* Admin preview banner — rendered via a portal to <body> with a
          z-index above the Layout header (z-50) so the Close button is
          always clickable. A slim admin bar at the very top of the
          viewport is a common pattern; the site header's top edge is
          intentionally covered while preview is active. Adds matching
          top padding to <body> so nothing visually shifts below the
          bar. */}
      {isPreview && createPortal(
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-[#FEF3C7] border-b-2 border-[#F59E0B] px-4 py-2 flex items-center justify-between text-sm shadow-md"
             style={{ height: '40px' }}
             data-testid="crisis-preview-banner">
          <div className="flex items-center gap-2 text-[#78350F] min-w-0">
            <span className="inline-block bg-[#F59E0B] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded flex-shrink-0">
              Preview
            </span>
            <span className="font-medium truncate">
              Admin preview — not visible to the public until
              &ldquo;Show Crisis Response Section publicly&rdquo; is on.
            </span>
          </div>
          <button
            onClick={() => { window.location.search = ''; }}
            className="text-xs text-[#78350F] hover:text-[#0A0A0A] underline font-semibold flex-shrink-0 ml-3"
            data-testid="crisis-preview-close">
            Close preview
          </button>
        </div>,
        document.body,
      )}

      {/* Hero Section */}
      {secOn('hero') && (
      <section className="relative" data-testid="country-hero">
        <div
          className="h-[340px] relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, #0A0A0A 0%, #1a1a2e 50%, #0A0A0A 100%)`,
          }}
        >
          {/* Optional admin-configured background image or slideshow.
              Section key matches the country program slug verbatim
              (e.g. programId 'colombia-yumbo' → 'country-colombia-yumbo-hero').
              Renders nothing (transparent) when unconfigured — the
              gradient behind shows through. */}
          <div className="absolute inset-0" data-testid="country-hero-media">
            <SectionMedia
              sectionKey={`country-${programId || ''}-hero`}
              className="w-full h-full opacity-70"
              priority
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70
                        via-black/40 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="px-6 w-full">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-4xl" role="img" aria-label={`${program.country_name} flag`}>
                  {COUNTRY_FLAGS[program.country_name]?.emoji || '\u{1F30D}'}
                </span>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#FCA5A5]" />
                  <span className="text-sm text-white/80 uppercase tracking-wider">
                    {program.country_name} — {program.program_city}
                  </span>
                </div>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3" data-testid="program-title">
                {program.program_title}
              </h1>
              <p className="text-white/90 text-base max-w-xl mb-5">
                {program.hero_copy}
              </p>
              <a href="#donate-section" className="btn-primary inline-flex text-sm" data-testid="hero-cta">
                {program.primary_cta}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Impact Tracker */}
      <ImpactTracker program={program} familiesFromDonations={familiesFromDonations} progressPercentage={progressPercentage} candleCount={candleCount} costPerFamily={costPerFamily} />

      {/* Active campaigns for this country — stacked list. Each links
          to its own /campaigns/{slug} page so donors can drill in. */}
      {countryCampaigns.length > 0 && (
        <section className="py-10 bg-white border-b border-[#E5E7EB]"
                 data-testid="country-campaigns-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA1826] font-bold mb-1">
                  Active Campaigns
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A]">
                  Support this program directly
                </h2>
              </div>
              <Link to="/campaigns"
                    className="text-sm text-[#BA1826] hover:underline inline-flex items-center gap-1"
                    data-testid="view-all-campaigns-link">
                All campaigns <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-3" data-testid="country-campaigns-list">
              {countryCampaigns.map((cc) => {
                const pct = cc.goal > 0
                  ? Math.min(100, (cc.total_raised / cc.goal) * 100) : 0;
                return (
                  <Link key={cc.id} to={`/campaigns/${cc.slug}`}
                        data-testid={`country-campaign-row-${cc.slug}`}
                        className="group flex flex-col sm:flex-row items-stretch bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#BA1826] rounded-lg overflow-hidden transition-colors">
                    {cc.media_url && !/\.(mp4|webm|mov)(\?|$)/i.test(cc.media_url) && (
                      <div className="sm:w-40 aspect-video sm:aspect-auto flex-shrink-0 overflow-hidden bg-[#E5E7EB]">
                        <img src={cc.media_url} alt={cc.name} loading="lazy"
                             className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </div>
                    )}
                    <div className="flex-1 p-4">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-white border border-[#E5E7EB] text-[#4B5563]">
                          {(cc.type || 'campaign').replace(/_/g, ' ')}
                        </span>
                      </div>
                      <h3 className="font-heading font-bold text-base text-[#0A0A0A] mb-1">{cc.name}</h3>
                      {cc.description && (
                        <p className="text-xs text-[#4B5563] line-clamp-2 mb-3">{cc.description}</p>
                      )}
                      {cc.goal > 0 && (
                        <>
                          <div className="relative h-1.5 bg-white border border-[#E5E7EB] rounded-full overflow-hidden mb-1">
                            <div className="absolute inset-y-0 left-0 bg-[#BA1826] rounded-full"
                                 style={{ width: `${pct}%` }}></div>
                          </div>
                          <p className="text-[11px] text-[#6B7280]">
                            <span className="font-bold text-[#0A0A0A]">
                              ${(cc.total_raised || 0).toLocaleString('en-US', {maximumFractionDigits: 0})}
                            </span>
                            <span> raised of </span>
                            <span>${(cc.goal || 0).toLocaleString('en-US', {maximumFractionDigits: 0})}</span>
                            <span> · {cc.donor_count || 0} donors</span>
                          </p>
                        </>
                      )}
                    </div>
                    <div className="flex items-center px-4 pb-4 sm:pb-0 sm:pr-6">
                      <span className="text-sm text-[#BA1826] font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                        Donate <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Candle Wall — right below impact stats for emotional connection */}
      <CandleWall programId={programId} programName={`${program.country_name} — ${program.program_city || program.program_title}`} />

      {/* Program Overview */}
      {secOn('overview') && (
        <ProgramOverview program={program} />
      )}

      {/* Emergency Support — crisis-response tile with its own donate CTA. */}
      {emergencyOn && (
        <section className="py-10 bg-gradient-to-br from-[#FEF2F2] to-[#FEE2E2] border-y-2 border-[#FCA5A5]"
                 data-testid="emergency-support-section">
          <div className="px-6 max-w-3xl mx-auto">
            <div className="flex items-center gap-2 justify-center mb-3">
              <AlertTriangle className="w-6 h-6 text-[#B91C1C]" />
              <p className="text-xs uppercase tracking-[0.15em] text-[#B91C1C] font-bold">
                Emergency Response
              </p>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0A0A0A] text-center mb-4"
                data-testid="emergency-title">
              {program.emergency_support_title
                || (isPreview && crisisPublic?.name)
                || 'Emergency Response'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 items-center">
              {(() => {
                // Prefer per-campaign media, fall back to country
                // program's emergency image.
                const src = (crisisPublic?.media_url && crisisPublic.media_url.trim())
                  || program.emergency_support_image;
                if (!src) {
                  return (
                    <div className="rounded-lg w-full h-56 bg-[#FECACA] flex items-center justify-center">
                      <AlertTriangle className="w-12 h-12 text-[#B91C1C]/60" />
                    </div>
                  );
                }
                if (/\.(mp4|webm|mov)(\?|$)/i.test(src)) {
                  return (
                    <video src={src} controls playsInline
                           className="rounded-lg w-full h-56 object-cover shadow-md bg-black"
                           data-testid="emergency-video">
                      Your browser cannot play this video.
                    </video>
                  );
                }
                return (
                  <img src={src}
                       alt={program.emergency_support_title || 'Emergency response'}
                       className="rounded-lg w-full h-56 object-cover shadow-md"
                       data-testid="emergency-image" />
                );
              })()}
              <div>
                <p className="text-sm text-[#4B5563] leading-relaxed mb-4"
                   data-testid="emergency-description">
                  {program.emergency_support_description
                    || (isPreview && crisisPublic?.description)
                    || ''}
                </p>
                {/* Live Impact — only rendered when a linked campaign is
                    both public AND has the live impact toggle on. */}
                {crisisPublic && crisisPublic.show_live_impact
                    && (crisisPublic.total_raised != null) && (
                  <div className="bg-white/70 border border-[#FCA5A5] rounded-lg p-3 mb-4"
                       data-testid="live-impact-widget">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="font-semibold text-[#B91C1C]">Live impact</span>
                      <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Updated in real time</span>
                    </div>
                    <p className="text-sm font-heading text-[#0A0A0A]" data-testid="live-impact-line">
                      <b>${Number(crisisPublic.total_raised || 0).toLocaleString()}</b> raised
                      {crisisPublic.goal ? <> of <b>${Number(crisisPublic.goal).toLocaleString()}</b></> : null}
                      <span className="text-[#6B7280]"> • </span>
                      <b>${Number(crisisPublic.distributed_amount || 0).toLocaleString()}</b> distributed
                    </p>
                    {crisisPublic.goal > 0 && (
                      <div className="mt-2 h-1.5 bg-[#FEE2E2] rounded overflow-hidden">
                        <div className="h-full bg-[#B91C1C]"
                          style={{ width: `${Math.min(100, (crisisPublic.total_raised / crisisPublic.goal) * 100)}%` }} />
                      </div>
                    )}
                  </div>
                )}
                {program.emergency_support_cost > 0 && (
                  <button onClick={handleEmergencyDonate}
                    disabled={emergencyProcessing || program.donations_enabled === false}
                    className="w-full bg-[#B91C1C] text-white font-semibold py-3 rounded-lg hover:bg-[#991B1B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="emergency-donate-btn">
                    {emergencyProcessing
                      ? 'Processing…'
                      : `${program.emergency_support_cta || 'Support Emergency Response'} — $${program.emergency_support_cost}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Locations / Areas within country */}
      {secOn('locations') && Array.isArray(program.locations) && program.locations.length > 0 && (
        <section className="py-10 bg-white" data-testid="locations-section">
          <div className="px-6 max-w-3xl mx-auto">
            <div className="text-center mb-6">
              <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-2">
                Where We Work
              </p>
              <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">
                Locations in {program.country_name}
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {program.locations.map((loc, idx) => (
                <div key={loc.id || idx}
                     className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
                     data-testid={`location-card-${idx}`}>
                  {loc.image ? (
                    <img src={loc.image} alt={loc.name}
                         className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-[#F1F5F9] flex items-center justify-center">
                      <MapPin className="w-10 h-10 text-[#9CA3AF]" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-[#BA1826]" />
                      <h3 className="font-heading font-bold text-base text-[#0A0A0A]">
                        {loc.name}
                      </h3>
                    </div>
                    {loc.description && (
                      <p className="text-xs text-[#4B5563] leading-relaxed">
                        {loc.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Three-Pillar Support System */}
      {(secOn('food_bucket') || secOn('hygiene_kit') || secOn('water')) && (
        <ThreePillarSystem program={program} foodItems={foodItems} hygieneItems={hygieneItems} foodCost={foodCost} hygieneCost={hygieneCost} waterCost={waterCost} foodSize={foodSize} hygieneSize={hygieneSize} fullFamilyCost={fullFamilyCost} sectionsEnabled={program.sections_enabled} />
      )}

      {/* Clean Water Access */}
      {secOn('water') && (
        <WaterSection program={program} waterCost={waterCost} waterProcessing={waterProcessing} handleWaterDonate={handleWaterDonate} />
      )}

      {/* Scaling Section */}
      <ScalingSection program={program} />

      {/* Donation Section */}
      {secOn('donation_tiers') && (
      <>
      {program.donations_enabled === false || program.status === 'pending' ? (
        <section id="donate-section" className="py-10 bg-white" data-testid="donate-section">
          <div className="px-6 text-center max-w-lg mx-auto">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-8">
              <p className="font-heading text-lg font-bold text-amber-800 mb-2">
                {program.status === 'pending' ? 'Program Pending' : 'Launching Soon'}
              </p>
              <p className="text-sm text-amber-700">
                {program.status === 'pending'
                  ? `We are currently finalizing our partnership in ${program.country_name}. Donations for this program will open once the program is fully established.`
                  : `Our ${program.country_name} program is launching soon through trusted partnerships. Please check back as we finalize the details, or consider supporting another active program in the meantime.`}
              </p>
            </div>
          </div>
        </section>
      ) : (
      <section id="donate-section" className="py-10 bg-white" data-testid="donate-section">
        <div className="px-6">
          <div className="text-center mb-8">
            <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-2">
              Make Your Impact
            </p>
            <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">
              {program.primary_cta}
            </h2>
            <p className="text-sm text-[#4B5563]">
              ${parseFloat(costPerFamily).toFixed(2)} supports 1 family in {program.program_city} for 1 month.
            </p>
          </div>

          {/* One-time / Monthly Toggle */}
          <div className="max-w-lg mx-auto mb-6">
            <div className="flex bg-[#F1F5F9] rounded-lg p-1">
              <button
                onClick={() => setIsRecurring(false)}
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                  !isRecurring ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[#4B5563]'
                }`}
                data-testid="one-time-toggle"
              >
                One-Time Gift
              </button>
              <button
                onClick={() => setIsRecurring(true)}
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1.5 ${
                  isRecurring ? 'bg-[#BA1826] text-white shadow-sm' : 'text-[#4B5563]'
                }`}
                data-testid="monthly-toggle"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Sponsor Monthly
              </button>
            </div>
          </div>

          {/* Donation Tiers */}
          <div className="max-w-lg mx-auto mb-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {tiers.map((tier) => {
                const isSelected = selectedTier?.id === tier.id;
                const tierClass = isSelected
                  ? 'border-[#BA1826] bg-[#FEF2F2]'
                  : tier.highlight
                    ? 'border-[#BA1826]/40 bg-white hover:border-[#BA1826]'
                    : 'border-[#E5E7EB] bg-white hover:border-[#BA1826]';
                return (
                <button
                  key={tier.id}
                  onClick={() => { setSelectedTier(tier); setCustomAmount(''); }}
                  className={`relative p-4 text-center border-2 rounded-lg transition-all ${tierClass}`}
                  data-testid={`tier-${tier.id}`}
                >
                  {tier.highlight && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-[#BA1826] text-white px-2 py-0.5 rounded-full">
                      POPULAR
                    </span>
                  )}
                  <div className="text-xl font-bold text-[#0A0A0A] mb-0.5">${tier.amount}</div>
                  <div className="text-xs font-medium text-[#BA1826]">{tier.label}</div>
                  {isRecurring && <span className="text-[10px] text-[#4B5563]">/month</span>}
                </button>
              );
              })}

              {/* Custom Amount */}
              <button
                onClick={() => { setSelectedTier(null); }}
                className={`p-4 text-center border-2 rounded-lg transition-all ${
                  !selectedTier && customAmount ? 'border-[#BA1826] bg-[#FEF2F2]' : 'border-[#E5E7EB] bg-white hover:border-[#BA1826]'
                }`}
              >
                <div className="text-xs font-medium text-[#4B5563] mb-1">Custom</div>
                <div className="flex items-center border border-[#E5E7EB] rounded overflow-hidden">
                  <span className="text-sm font-semibold text-[#4B5563] px-1.5 bg-[#F8F9FA]">$</span>
                  <input
                    type="number"
                    min="1"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedTier(null); }}
                    className="w-full px-1.5 py-1 text-sm text-center focus:outline-none"
                    placeholder="..."
                    data-testid="custom-amount-input"
                  />
                </div>
              </button>
            </div>
          </div>

          {/* Donor Info & Submit */}
          <div className="max-w-lg mx-auto">
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826]"
                  data-testid="donor-name-input"
                />
                <input
                  type="email"
                  placeholder="Your email (optional)"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826]"
                  data-testid="donor-email-input"
                />
                <textarea
                  placeholder="Why I chose to support this country (optional)"
                  value={donorNote}
                  onChange={(e) => setDonorNote(e.target.value)}
                  maxLength={500}
                  rows={2}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] resize-none"
                  data-testid="donor-note-input"
                />
              </div>

              {/* Wall Display Preference */}
              <div className="mb-4 p-3 bg-[#F8F9FA] rounded-sm">
                <p className="text-xs font-medium text-[#0A0A0A] mb-2">Display preference</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'full', label: 'Name & amount' },
                    { value: 'name_only', label: 'Name only' },
                    { value: 'anonymous', label: 'Anonymous' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setWallDisplay(opt.value)}
                      className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                        wallDisplay === opt.value
                          ? 'bg-[#BA1826] text-white'
                          : 'bg-white border border-[#E5E7EB] text-[#4B5563]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              {currentAmount > 0 && (
                <div className="mb-4 p-3 bg-[#FEFCE8] border border-[#FDE68A] rounded-sm">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4B5563]">
                      {isRecurring ? 'Monthly donation' : 'One-time donation'}
                    </span>
                    <span className="font-bold text-[#0A0A0A]">
                      ${currentAmount}{isRecurring ? '/mo' : ''}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-[#4B5563]">Program</span>
                    <span className="font-medium text-[#BA1826]">
                      {program.country_name} — {program.program_title}
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={handleDonate}
                disabled={isProcessing || currentAmount <= 0}
                className="btn-primary w-full py-3 disabled:opacity-50"
                data-testid="donate-submit-btn"
              >
                {isProcessing
                  ? 'Processing...'
                  : isRecurring 
                    ? `${program.recurring_cta} — $${currentAmount}/mo`
                    : `${program.primary_cta} — $${currentAmount}`
                }
              </button>

              <p className="text-xs text-[#4B5563] text-center mt-3">
                Secure payment powered by Stripe. Tax-deductible.
              </p>
            </div>
          </div>
        </div>
      </section>
      )}
      </>
      )}

      {/* Transparency Section */}
      {secOn('transparency') && (
      <section className="py-10 bg-[#F8F9FA]" data-testid="transparency-section">
        <div className="px-6 max-w-xl mx-auto text-center">
          <ShieldCheck className="w-8 h-8 text-[#BA1826] mx-auto mb-3" />
          <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-3">
            Transparent Fund Tracking
          </h3>
          <p className="text-sm text-[#4B5563] leading-relaxed">
            {program.transparency_copy}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            <span className="text-xs bg-white border border-[#E5E7EB] text-[#4B5563] px-3 py-1 rounded-full">
              Country-specific tracking
            </span>
            <span className="text-xs bg-white border border-[#E5E7EB] text-[#4B5563] px-3 py-1 rounded-full">
              Monthly progress updates
            </span>
            <span className="text-xs bg-white border border-[#E5E7EB] text-[#4B5563] px-3 py-1 rounded-full">
              Full financial visibility
            </span>
          </div>
        </div>
      </section>
      )}

      {/* Media Gallery */}
      {secOn('gallery') && (
      <section className="py-10 bg-white" data-testid="program-gallery">
        <div className="px-6 max-w-xl mx-auto">
          <div className="text-center mb-6">
            <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] mb-1">
              Program Gallery
            </h3>
            <p className="text-xs text-[#4B5563]">
              Photos and videos from our work in {program.program_city}
            </p>
          </div>

          {media.length === 0 ? (
            <div className="text-center py-8">
              <ImageIcon className="w-8 h-8 text-[#E5E7EB] mx-auto mb-3" />
              <p className="text-sm text-[#4B5563]">
                Distribution photos and community images from {program.program_city} will be added here as the program launches.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {media.map((item) => (
                <div
                  key={item.id}
                  className="relative group rounded-lg overflow-hidden bg-[#F8F9FA] cursor-pointer"
                  onClick={() => setLightboxItem(item)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  {item.type === 'video' ? (
                    <div className="relative aspect-video bg-black">
                      <video
                        src={item.url}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="w-5 h-5 text-[#BA1826] ml-0.5" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video">
                      <img
                        src={item.url}
                        alt={item.caption || 'Program photo'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  {item.caption && (
                    <p className="text-xs text-[#4B5563] p-2 text-center">{item.caption}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* Lightbox */}
      {lightboxItem && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxItem(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
            onClick={() => setLightboxItem(null)}
          >
            <XIcon className="w-8 h-8" />
          </button>
          <div className="max-w-3xl w-full max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            {lightboxItem.type === 'video' ? (
              <video
                src={lightboxItem.url}
                controls
                autoPlay
                className="w-full max-h-[80vh] rounded-lg"
                data-testid="lightbox-video"
              />
            ) : (
              <img
                src={lightboxItem.url}
                alt={lightboxItem.caption || 'Program photo'}
                className="w-full max-h-[80vh] object-contain rounded-lg"
                data-testid="lightbox-image"
              />
            )}
            {lightboxItem.caption && (
              <p className="text-white/80 text-sm text-center mt-3">{lightboxItem.caption}</p>
            )}
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-10 bg-[#BA1826]" data-testid="bottom-cta">
        <div className="px-6 text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-3">
            {program.impact_line}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#donate-section" className="bg-white text-[#BA1826] px-6 py-2 font-semibold rounded hover:bg-gray-100 transition-all text-sm">
              {program.primary_cta}
            </a>
            <Link to="/international" className="bg-transparent text-white border-2 border-white px-6 py-2 font-semibold rounded hover:bg-white/10 transition-all text-sm">
              View All Programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CountryProgram;
